import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import React, { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useParticipantLandedDetails } from "../../../../../api/services/program/programApis";
import {
  FormController,
  MuiBox,
  MuiDateField,
  MuiGrid,
  MuiLink,
  MuiModal,
  MuiMultiSelect,
  MuiOptionMenu,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  TableHeaderSearchIcon,
} from "../../../../../components";
import MuiEllipseLabel from "../../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  DATE_FORMAT,
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
  PAGE_IDS,
  PAGE_URLS,
} from "../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";
import { LabelValue } from "../../../../../helpers/types/common";
import {
  buildActionMenuItems,
  checkFilterMatch,
  compareDates,
  convertToUserFormat,
  getUniqueCountriesFromData,
  getValueOrBlank,
  isDateInRange,
  isNotEmpty,
  stringIncludesArrayOfStringsValue,
} from "../../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../hooks/useToast";
import { siteCoreContentLoadingSelector } from "../../../../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../../../../redux/slices/appState/appStateSlice";
import {
  ParticipantLandedResultForm,
  ParticipantLandedResultFormKey,
  ParticipantLandedResultRowValue,
} from "../../../modelsAndSchemas/Models";
import { myCaseloadsSiteCoreKeys } from "../../../sitecore/SiteCoreKeys";
import { ParticipantLandedWrapper } from "./ParticipantLanded.style";

export interface ParticipantLandedProps {
  open: boolean;
  onClose: () => void;
}
const pageId = PAGE_IDS.COACH_PORTAL_DASHBOARD_HEADER_PARTICIPANT_LANDED;
export const ParticipantLanded: FC<ParticipantLandedProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { useFetchLabel, commonLabels } = useSiteCore();
  const navigate = useNavigate();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const multiSelectARIALabels = useMultiSelectARIALabels();

  const {
    isPending: isParticipantLandedPending,
    data: participantLandedResult,
    isError: isParticipantLandedError,
  } = useParticipantLandedDetails({
    loggedInCoachId: getValueOrBlank(loggedInUser?.id),
  });

  const tableRef = React.useRef<{ resetPagination: () => void }>(null);
  const [filteredResultsFields, setFilteredResultsFields] = useState<
    ParticipantLandedResultRowValue[]
  >([]);
  const [uniqueCountriesFields, setUniqueCountriesFields] = useState<
    LabelValue[]
  >([]);
  const [updatedRowsFields, setUpdatedRowsFields] = useState<
    ParticipantLandedResultRowValue[]
  >([]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        CPDashboardLoading: {
          isVisible: isParticipantLandedPending,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          CPDashboardLoading: {
            isVisible: false,
          },
        }),
      );
    };
  }, [isParticipantLandedPending]);

  useEffect(() => {
    if (isParticipantLandedError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ CPDashboardLoading: { isVisible: false } }));
    }
  }, [isParticipantLandedError]);

  const {
    myCaseloadsTitle,
    participantsLandedPopupHeader,
    noCaseloadsAvailable,
    participantNameTableColumn: participantNameLabel,
    programTemplateTableColumn: programTemplateLabel,
    programEndDateTableColumn: programEndDateLabel,
    participantEmailTableColumn: participantEmailLabel,
    participantMobileNumberTableColumn: participantMobileNumberLabel,
    projectManagerTableColumn: projectManagerLabel,
    countryTableColumn: countryLabel,
    programStartMonthYearTableColumn: programStartMonthYearLabel,
    actionTableColumn: actionLabel,
    actionTableColumnManage: manageLabel,
  } = myCaseloadsSiteCoreKeys;
  const getLabel = useFetchLabel(myCaseloadsTitle);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const { control, setValue, getValues, reset } =
    useForm<ParticipantLandedResultForm>({
      mode: "all",
      defaultValues: {
        [ParticipantLandedResultFormKey.participantName]: "",
        [ParticipantLandedResultFormKey.programTemplateName]: "",
        [ParticipantLandedResultFormKey.programEndDate]: "",
        [ParticipantLandedResultFormKey.participantEmail]: "",
        [ParticipantLandedResultFormKey.participantMobileNumber]: "",
        [ParticipantLandedResultFormKey.projectManager]: "",
        [ParticipantLandedResultFormKey.countryIds]: [],
        [ParticipantLandedResultFormKey.programStartDate]: "",
      },
    });
  const formValues = useWatch({ control });

  useEffect(() => {
    resetPagination();
    const rows = [...filteredResultsFields];
    const uniqueCountries = getUniqueCountriesFromData(rows);
    setUniqueCountriesFields(uniqueCountries);
  }, [filteredResultsFields]);

  useEffect(() => {
    if (!siteCoreLoading) {
      const localRows = [...(participantLandedResult || [])];
      setUpdatedRowsFields(localRows);
      setFilteredResultsFields([...localRows]);
      reset(); // Reset form only when initial data loads
    }
  }, [participantLandedResult, siteCoreLoading]);

  useEffect(() => {
    if (isNotEmpty(updatedRowsFields)) {
      const {
        participantName,
        programTemplateName,
        countryIds,
        participantEmail,
        participantMobileNumber,
        projectManager,
        programStartDate,
        programEndDate,
      } = getValues();

      const myData = updatedRowsFields.filter((item) => {
        const isProgramTemplateMatch = checkFilterMatch(
          programTemplateName,
          item.programTemplateName,
        );

        const isParticipantNameMatch = checkFilterMatch(
          getValueOrBlank(participantName),
          getValueOrBlank(item.participantName),
        );

        const isParticipantMobileNumberMatch = checkFilterMatch(
          getValueOrBlank(participantMobileNumber),
          getValueOrBlank(item.participantMobileNumber),
        );

        const isParticipantEmailMatch = checkFilterMatch(
          getValueOrBlank(participantEmail),
          getValueOrBlank(item.participantEmail),
        );

        const isProjectManagerMatch = checkFilterMatch(
          getValueOrBlank(projectManager),
          getValueOrBlank(item.projectManager),
        );

        const countryMatch = stringIncludesArrayOfStringsValue(
          countryIds,
          item.country,
        );

        let isProgramStartDateMatch = true;

        if (programStartDate) {
          if (!item?.programStartDate) {
            isProgramStartDateMatch = false;
          } else {
            const selectedDateField = dayjs(
              programStartDate,
              userCountryDateFormat,
            );
            const startOfSelectedDateMonthField =
              selectedDateField.startOf("month");
            const endOfSelectedDateMonthField =
              selectedDateField.endOf("month");

            const itemDate = dayjs(item?.programStartDate);

            isProgramStartDateMatch = isDateInRange(
              itemDate,
              startOfSelectedDateMonthField,
              endOfSelectedDateMonthField,
            );
          }
        }
        const isProgramEndDateMatch = compareDates(
          getValueOrBlank(programEndDate),
          getValueOrBlank(item?.programEndDate),
          getValueOrBlank(userCountryDateFormat),
        );

        return (
          isProgramTemplateMatch &&
          isProgramStartDateMatch &&
          isProgramEndDateMatch &&
          countryMatch &&
          isParticipantNameMatch &&
          isParticipantMobileNumberMatch &&
          isParticipantEmailMatch &&
          isProjectManagerMatch
        );
      });
      setFilteredResultsFields([...myData]);
      resetPagination();
    }
  }, [formValues]);

  const handleManageActionClick = (id: string) => {
    navigate(`${PAGE_URLS.COACH_PORTAL_VIEW_PARTICIPANT}/${id}`);
    onClose();
  };

  const columns = [
    {
      id: ParticipantLandedResultFormKey.participantName,
      label: getLabel(participantNameLabel),
      isEmptyValue: true,
      isSortVisible: true,
      className: "cell-participant-name",
      render: (row: any) => {
        if (!row.participantName) return null;
        else {
          return hasModulePermission(MODULE_PERMISSIONS.COACH_PORTAL_VIEW) ? (
            <MuiLink
              onClick={() => handleManageActionClick(getValueOrBlank(row.id))}
              id={`${pageId}-manage-participant`}
              data-testid="manage-participant-link"
            >
              <MuiBox data-testid="participant-name-link">
                <MuiEllipseLabel
                  data-testid="participant-name-link"
                  labelTypoClassName="Link participant-name-link"
                  text={row.participantName}
                  labelTypoVariant="h3"
                  placement="bottom-start"
                  pointerCursor
                />
              </MuiBox>
            </MuiLink>
          ) : (
            <MuiBox>
              <MuiTypography variant="h3" className="">
                {row.participantName}
              </MuiTypography>
            </MuiBox>
          );
        }
      },
    },
    {
      id: ParticipantLandedResultFormKey.programTemplateName,
      label: getLabel(programTemplateLabel),
      isSortVisible: true,
    },
    {
      id: ParticipantLandedResultFormKey.programEndDate,
      label: getLabel(programEndDateLabel),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {isNotEmpty(row.programEndDate)
              ? convertToUserFormat(row.programEndDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ParticipantLandedResultFormKey.participantEmail,
      label: getLabel(participantEmailLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: ParticipantLandedResultFormKey.participantMobileNumber,
      label: getLabel(participantMobileNumberLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: ParticipantLandedResultFormKey.projectManager,
      label: getLabel(projectManagerLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: ParticipantLandedResultFormKey.countryName,
      label: getLabel(countryLabel),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-country",
    },
    {
      id: ParticipantLandedResultFormKey.programStartDate,
      label: getLabel(programStartMonthYearLabel),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiTypography variant="h3" className="">
          {isNotEmpty(row.programStartDate)
            ? dayjs(row.programStartDate).format(DATE_FORMAT.MMMM_YYYY)
            : EMPTY_VALUE_DASH}
        </MuiTypography>
      ),
    },
    {
      id: "action",
      label: getLabel(actionLabel),
      render: (row: RowProps) => {
        const actions = [
          {
            permissionKey: MODULE_PERMISSIONS.COACH_PORTAL_EDIT,
            create: () => ({
              onClick: () => handleManageActionClick(getValueOrBlank(row.id)),
              dataTestId: `${pageId}-manage`,
              label: getLabel(manageLabel),
              ariaLabel: getLabel(manageLabel),
            }),
          },
        ];

        const menuItems = buildActionMenuItems(
          row,
          actions,
          hasModulePermission,
        );

        return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
      },
    },
  ];

  const handleMuiDateChange = (key: string, newValue: dayjs.Dayjs | null) => {
    setValue(
      key,
      newValue && newValue.isValid()
        ? newValue.format(userCountryDateFormat)
        : "",
    );
  };

  const renderSearchIcon = () => <TableHeaderSearchIcon />;

  const tableHeaderForm = [
    <FormController
      control={control}
      name={ParticipantLandedResultFormKey.participantName}
      key={ParticipantLandedResultFormKey.participantName}
    >
      <MuiTextField
        id={`${pageId}-${ParticipantLandedResultFormKey.participantName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ParticipantLandedResultFormKey.programTemplateName}
      key={ParticipantLandedResultFormKey.programTemplateName}
    >
      <MuiTextField
        id={`${pageId}-${ParticipantLandedResultFormKey.programTemplateName}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ParticipantLandedResultFormKey.programEndDate}
      key={ParticipantLandedResultFormKey.programEndDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          value={
            getValues().programEndDate !== ""
              ? dayjs(getValues().programEndDate, userCountryDateFormat)
              : null
          }
          format={userCountryDateFormat}
          valueFormat={userCountryDateFormat}
          clearable
          onChange={(newValue) => {
            setValue(
              ParticipantLandedResultFormKey.programEndDate,
              getValueOrBlank(newValue?.format(userCountryDateFormat)),
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      control={control}
      name={ParticipantLandedResultFormKey.participantEmail}
      key={ParticipantLandedResultFormKey.participantEmail}
    >
      <MuiTextField
        id={`${pageId}-${ParticipantLandedResultFormKey.participantEmail}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ParticipantLandedResultFormKey.participantMobileNumber}
      key={ParticipantLandedResultFormKey.participantMobileNumber}
    >
      <MuiTextField
        id={`${pageId}-${ParticipantLandedResultFormKey.participantMobileNumber}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ParticipantLandedResultFormKey.projectManager}
      key={ParticipantLandedResultFormKey.projectManager}
    >
      <MuiTextField
        id={`${pageId}-${ParticipantLandedResultFormKey.projectManager}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ParticipantLandedResultFormKey.countryIds}
      key={ParticipantLandedResultFormKey.countryIds}
    >
      <MuiMultiSelect
        label=""
        disabled={false}
        showSearch={true}
        showChip={false}
        textFieldWidth={0}
        options={uniqueCountriesFields}
        id={`${pageId}-${ParticipantLandedResultFormKey.countryIds}`}
        selectPlaceHolder={commonLabels.selectPlaceHolder}
        searchPlaceHolder={commonLabels.search}
        selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
        doneText={commonLabels.done}
        allARIAProps={multiSelectARIALabels}
      />
    </FormController>,
    <FormController
      control={control}
      name={ParticipantLandedResultFormKey.programStartDate}
      key={ParticipantLandedResultFormKey.programStartDate}
    >
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        localeText={{
          fieldMonthPlaceholder: () => commonLabels.monthPlaceHolder,
          fieldYearPlaceholder: () => commonLabels.yearPlaceHolder,
        }}
      >
        <MuiDateField
          value={
            getValues().programStartDate !== ""
              ? dayjs(getValues().programStartDate, userCountryDateFormat)
              : null
          }
          format={DATE_FORMAT.MMM_YYYY_SLASH}
          views={["month", "year"]}
          clearable
          onChange={(newValue) =>
            handleMuiDateChange(
              ParticipantLandedResultFormKey.programStartDate,
              newValue,
            )
          }
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,

    <></>,
  ];

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      title={getLabel(participantsLandedPopupHeader)}
      hasLargeContent
      showPrimaryButton={false}
      secondaryButtonTitle={commonLabels.close}
      onSecondaryButtonClick={onClose}
    >
      <ParticipantLandedWrapper>
        <MuiGrid container>
          <MuiGrid container>
            <MuiGrid size={{ xs: 12 }} className="header-right">
              <MuiTable
                ref={tableRef}
                pageId={pageId}
                tableHeaderForm={!siteCoreLoading ? tableHeaderForm : []}
                columns={columns}
                tableResults={[...filteredResultsFields]}
                noDataInApi={participantLandedResult?.length === 0}
                emptyMessage={getLabel(noCaseloadsAvailable)}
                noResultMessage={commonLabels.noResult}
                priorityResults={[]}
                isLastColumnSticky
                isShowDashIfValueEmpty
              />
            </MuiGrid>
          </MuiGrid>
        </MuiGrid>
      </ParticipantLandedWrapper>
    </MuiModal>
  );
};
